import { useQuery } from '@tanstack/react-query';
import { projectsApi, type ProjectsQueryParams } from '@/services/api';
import type { ProjectFormData } from '@/types';
import { useMutationWithToast } from './useMutationWithToast';
import { forecastKeys } from './useForecast';

export const projectKeys = {
    all: ['projects'] as const,
    simple: () => [...projectKeys.all, 'simple'] as const,
    list: (params?: ProjectsQueryParams) => [...projectKeys.all, 'list', params] as const,
    detail: (id: string) => [...projectKeys.all, 'detail', id] as const,
    financial: (id: string) => [...projectKeys.all, 'financial', id] as const,
};

export function useProjectsSimple(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: projectKeys.simple(),
        queryFn: () => projectsApi.getAllSimple(),
        enabled: options?.enabled ?? true,
    });
}

export function useProjects(params?: ProjectsQueryParams) {
    return useQuery({
        queryKey: projectKeys.list(params),
        queryFn: () => projectsApi.getAll(params),
    });
}

export function useProject(id: string) {
    return useQuery({
        queryKey: projectKeys.detail(id),
        queryFn: () => projectsApi.getById(id),
        enabled: !!id,
    });
}

export function useProjectFinancialSummary(id: string) {
    return useQuery({
        queryKey: projectKeys.financial(id),
        queryFn: () => projectsApi.getFinancialSummary(id),
        enabled: !!id,
    });
}

export function useCreateProject() {
    return useMutationWithToast({
        mutationFn: (data: ProjectFormData) => projectsApi.create(data),
        operationName: 'Création chantier',
        successMessage: 'Chantier créé avec succès',
        invalidateKeys: [projectKeys.all],
    });
}

export function useUpdateProject() {
    return useMutationWithToast({
        mutationFn: ({ id, data }: { id: string; data: Partial<ProjectFormData & { status: string }> }) =>
            projectsApi.update(id, data),
        operationName: 'Mise à jour chantier',
        successMessage: 'Chantier mis à jour',
        invalidateKeys: [projectKeys.all, forecastKeys.all],
    });
}

export function useDeleteProject() {
    return useMutationWithToast({
        mutationFn: (id: string) => projectsApi.delete(id),
        operationName: 'Suppression chantier',
        successMessage: 'Chantier supprimé',
        invalidateKeys: [projectKeys.all],
    });
}
